import { InjectionToken, inject } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { IChingLinePosition } from '../core/iching.models';
import { HdIChingAccessService } from '../hd-iching-access.service';
import { HdIChingPanelComponent } from '../panel/hd-iching-panel.component';
import { HD_ICHING_PANEL_KEY, HdIChingPanelData, HdIChingPanelTarget } from '../panel/hd-iching-panel.models';
import { HdIChingPanelService } from '../panel/hd-iching-panel.service';

/**
 * `true` binnen een modale dialoog (de I Tjing-momentdialoog). Het leespaneel
 * volgt daarbuiten de globale `gateInfoOpenBehavior`-setting, die op de
 * chart-pagina meestal "gedockt" is: het paneel landde dan in de dock áchter
 * de xl-dialoog en bleef onzichtbaar (bugfix 2026-10-18). Met dit token opent
 * het altijd zwevend; de `FloatingPanelService` legt een paneel dat na een
 * dialoog opent er boven.
 */
export const HD_ICHING_IN_MODAL = new InjectionToken<boolean>('HD_ICHING_IN_MODAL', { providedIn: 'root', factory: () => false });

export type IChingHexagramOpener = (number: number, options?: { readonly focusLine?: IChingLinePosition }) => void;

/**
 * Opent het klassieke leespaneel op een hexagram. Buiten een modale dialoog
 * via `HdIChingPanelService` (dock-/dialoogkeuze uit de setting); erbinnen
 * zwevend: nieuw zwevend, een gedockt paneel komt los, een zwevend paneel
 * komt naar voren. Zelfde key, component, data en dock-tab als
 * `HdIChingPanelService.open`, zodat het één en hetzelfde paneel blijft.
 * Aanroepen binnen een injectiecontext.
 */
export function injectIChingHexagramOpener(): IChingHexagramOpener {
  const panel = inject(HdIChingPanelService);
  if (!inject(HD_ICHING_IN_MODAL)) {
    return (number, options) => panel.openHexagram(number, options);
  }
  const floatingPanels = inject(FloatingPanelService);
  const access = inject(HdIChingAccessService);
  return (number, options) => {
    if (!access.classicVisible()) {
      return;
    }
    const target: HdIChingPanelTarget =
      options?.focusLine != null
        ? { view: 'hexagram', number, focusLine: options.focusLine }
        : { view: 'hexagram', number };
    const data: HdIChingPanelData = { target };
    const ref = floatingPanels.openOrFocus<HdIChingPanelComponent, HdIChingPanelData>({
      key: HD_ICHING_PANEL_KEY,
      component: HdIChingPanelComponent,
      data,
      dockTab: { label: 'IC', icon: 'scroll-old' },
      dockZone: 'full',
      openInitiallyDocked: false,
      redockOnReopen: false,
      bypassAutoDock: true,
      startPosition: 'center',
    });
    // Verse referentie: ook een al open paneel navigeert/scrollt opnieuw.
    ref.replaceData({ ...data });
  };
}
